import React, { useState } from 'react';
import { X, Copy, Check, Link as LinkIcon, Globe, Shield } from 'lucide-react';

const EmbedFormModal = ({ event, onClose }) => {
  const [copiedSecure, setCopiedSecure] = useState(false);
  const [copiedPublic, setCopiedPublic] = useState(false);
  const [secureLink, setSecureLink] = useState('');
  const [publicLink, setPublicLink] = useState('');

  const baseUrl = window.location.origin;

  const generateSecureLink = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v2/events/${event._id}/generate-secure-link`,
        { method: 'POST' }
      );
      const data = await response.json();
      if (data.success) {
        const link = `${baseUrl}/register/${data.data.token}`;
        setSecureLink(link);
        copyToClipboard(link, 'secure');
      }
    } catch (error) {
      console.error('Error generating secure link:', error);
    }
  };

  const generatePublicLink = () => {
    const link = `${baseUrl}/register/${event._id}`;
    setPublicLink(link);
    copyToClipboard(link, 'public');
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'secure') {
      setCopiedSecure(true);
      setTimeout(() => setCopiedSecure(false), 2000);
    } else {
      setCopiedPublic(true);
      setTimeout(() => setCopiedPublic(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <LinkIcon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Embed Form Links</h2>
                <p className="text-sm text-blue-100 mt-1">Generate and share your form links</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Secure Link */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
            <div className="flex items-start space-x-4 mb-4">
              <div className="p-3 bg-blue-600 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-lg flex items-center">
                  Secure Link
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                    One-time use
                  </span>
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Generate a secure, one-time use link that expires after an ID card is created.
                  Perfect for controlled access.
                </p>
              </div>
            </div>

            {secureLink ? (
              <div className="space-y-3">
                <div className="bg-white border border-blue-300 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <code className="text-sm text-blue-700 break-all flex-1">
                      {secureLink}
                    </code>
                    <button
                      onClick={() => copyToClipboard(secureLink, 'secure')}
                      className="ml-3 p-2 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                    >
                      {copiedSecure ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                  </div>
                </div>
                <button
                  onClick={generateSecureLink}
                  className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Generate New Secure Link
                </button>
              </div>
            ) : (
              <button
                onClick={generateSecureLink}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center"
              >
                <Shield className="w-5 h-5 mr-2" />
                Generate Secure Link
              </button>
            )}
          </div>

          {/* Public Link */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
            <div className="flex items-start space-x-4 mb-4">
              <div className="p-3 bg-purple-600 rounded-lg">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-lg flex items-center">
                  Public Create Form
                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                    Open access
                  </span>
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Create a public form link that anyone can use to generate an ID card.
                </p>
              </div>
            </div>

            {publicLink ? (
              <div className="space-y-3">
                <div className="bg-white border border-purple-300 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <code className="text-sm text-purple-700 break-all flex-1">
                      {publicLink}
                    </code>
                    <button
                      onClick={() => copyToClipboard(publicLink, 'public')}
                      className="ml-3 p-2 hover:bg-purple-50 rounded transition-colors flex-shrink-0"
                    >
                      {copiedPublic ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-purple-600" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={generatePublicLink}
                className="w-full py-3 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center justify-center"
              >
                <Globe className="w-5 h-5 mr-2" />
                Generate Public Link
              </button>
            )}
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Links are generated instantly and ready to share.
                Secure links expire after one use for better security control.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbedFormModal;